import { useCallback, useEffect, useRef, useState } from 'react';

// Three failed polls in a row and the interval stops until reload() (P-6).
export const MAX_POLL_FAILURES = 3;

// A server string (the 422 role answer is a plain string), else its message
// field, else the caller's fallback.
export const fleetErrorText = (err, fallback = 'Check your connection and try again.') => {
  const body = err?.response?.data;
  if (typeof body === 'string' && body) return body;
  return body?.message || fallback;
};

// One place for the load / poll semantics of every Devices page (P-6).
//
// load()      async, returns the value to store
// intervalMs  0 = no polling
// enabled     false = do nothing at all
// poll        false = the interval ticks do nothing; ONLY the interval is
//             gated: the first load, reload() and deps refetches are not, and
//             flipping it never refetches (it is read through a ref).
// deps        a change refetches at once without `loading` and re-arms the
//             interval; the interval always calls the newest `load`, so a
//             poll never sends a stale query.
//
// loading   true until the first answer (success or failure)
// fetching  a non-poll request (deps change, reload) is in flight
// error     the axios error of a failed FIRST load, kept whole so the page can
//           read err.response.data; null once something loaded
// onRefetchError(err)  a refetch the user caused (deps change, reload) failed
//           while earlier data is on screen; polls stay silent
// stalled   three consecutive polls failed and the interval stopped
const useFleetPoll = (
  load,
  { intervalMs = 0, enabled = true, poll = true, deps = [], onRefetchError } = {},
) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(null);
  const [stalled, setStalled] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const loadRef = useRef(load);
  loadRef.current = load;
  const onRefetchErrorRef = useRef(onRefetchError);
  onRefetchErrorRef.current = onRefetchError;
  const pollRef = useRef(poll);
  pollRef.current = poll;
  const hasData = useRef(false);
  const latest = useRef(0);

  const reload = useCallback(() => setReloadKey((n) => n + 1), []);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    let timer = null;
    let failures = 0;

    const stop = () => {
      if (timer) clearInterval(timer);
      timer = null;
    };

    const run = async (isPoll) => {
      const mine = latest.current + 1;
      latest.current = mine;
      if (!isPoll) {
        setFetching(true);
        if (!hasData.current) {
          setLoading(true);
          setError(null);
        }
      }
      try {
        const value = await loadRef.current();
        if (cancelled || mine !== latest.current) return;
        hasData.current = true;
        failures = 0;
        setData(value);
        setError(null);
        setStalled(false);
      } catch (err) {
        if (cancelled || mine !== latest.current) return;
        failures += 1;
        if (!hasData.current) {
          setError(err);
          stop();
        } else if (!isPoll) {
          onRefetchErrorRef.current?.(err);
        } else if (failures >= MAX_POLL_FAILURES) {
          setStalled(true);
          stop();
        }
      } finally {
        if (!cancelled && mine === latest.current) {
          setLoading(false);
          setFetching(false);
        }
      }
    };

    setStalled(false);
    run(false);
    if (intervalMs > 0) timer = setInterval(() => {
      if (pollRef.current) run(true);
    }, intervalMs);

    return () => {
      cancelled = true;
      stop();
    };
    // `deps` is the caller's own list; the rule cannot read through a spread.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, intervalMs, reloadKey, ...deps]);

  return { data, loading, fetching, error, stalled, reload };
};

export default useFleetPoll;
